import { Progress } from "@/components/ui/progress";

const handleJump = (targetId) => {
  if (typeof document === "undefined") return;
  const element = document.getElementById(targetId);
  if (!element) return;
  element.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const MiningSummaryPanel = ({ profile, smithing, jumpLinks = [] }) => {
  if (!profile) return null;
  return (
    <section data-testid="mining-summary-panel" className="border border-amber-900/30 bg-zinc-950/80 p-4 rounded-sm space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <div data-testid="mining-level-value" className="font-cinzel text-2xl text-amber-300 uppercase tracking-wide">Mining {profile.level}</div>
          <div data-testid="mining-level-subtitle" className="text-xs uppercase tracking-[0.25em] text-zinc-500 mt-1">Highest unlocked mine: {profile.highest_unlocked_mine_name}</div>
        </div>
        <div className="text-right">
          <div data-testid="mining-energy-value" className="text-lg font-cinzel text-zinc-100">Energy {profile.energy} / {profile.max_energy}</div>
          <div className="text-xs text-zinc-500 uppercase tracking-[0.2em] mt-1">Regen: {profile.energy_regen_amount} every {profile.energy_regen_minutes}m</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div data-testid="mining-discoveries-total" className="border border-zinc-800 bg-zinc-900/60 p-3 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Discoveries logged</div>
          <div className="font-cinzel text-xl text-amber-300 mt-2">{profile.discoveries_total?.toLocaleString?.() ?? 0}</div>
        </div>
        <div data-testid="mining-highest-mine-card" className="border border-zinc-800 bg-zinc-900/60 p-3 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Deepest unlocked mine</div>
          <div className="font-cinzel text-lg text-zinc-100 mt-2">{profile.highest_unlocked_mine_name}</div>
        </div>
        <div data-testid="mining-projects-completed-card" className="border border-zinc-800 bg-zinc-900/60 p-3 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Projects completed</div>
          <div className="font-cinzel text-xl text-zinc-100 mt-2">{profile.projects_completed ?? smithing?.project_summary?.completed_count ?? 0}</div>
        </div>
        <div data-testid="mining-project-bonus-card" className="border border-zinc-800 bg-zinc-900/60 p-3 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Permanent mining bonus</div>
          <div className="text-sm text-zinc-300 mt-2">
            {smithing?.project_summary?.bonuses?.raw_ore_yield_bonus
              ? `+${Math.round(smithing.project_summary.bonuses.raw_ore_yield_bonus * 100)}% ore yield`
              : smithing?.project_summary?.bonuses?.discovery_bonus
              ? `+${Math.round(smithing.project_summary.bonuses.discovery_bonus * 100)}% discovery chance`
              : smithing?.project_summary?.bonuses?.deposit_capacity_bonus
              ? `+${smithing.project_summary.bonuses.deposit_capacity_bonus} deposit capacity`
              : smithing?.project_summary?.bonuses?.deposit_restore_per_hour_bonus
              ? `+${smithing.project_summary.bonuses.deposit_restore_per_hour_bonus}/hr deposit recovery`
              : "No permanent project bonus yet."}
          </div>
        </div>
      </div>

      {smithing?.active_boost && (
        <div data-testid="mining-active-boost-card" className="border border-amber-900/30 bg-amber-950/10 p-3 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-amber-300">Next mining session boost ready</div>
          <div className="font-cinzel text-base text-zinc-100 mt-2">{smithing.active_boost.name}</div>
          <div className="text-xs text-zinc-400 mt-1">{smithing.active_boost.effect_summary}</div>
        </div>
      )}

      {smithing?.active_project && (
        <div data-testid="mining-active-project-summary-card" className="border border-amber-900/30 bg-amber-950/10 p-3 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-amber-300">Mining project underway</div>
          <div className="font-cinzel text-base text-zinc-100 mt-2">{smithing.active_project.name}</div>
          <div className="text-xs text-zinc-400 mt-1">{smithing.active_project.effect_summary}</div>
        </div>
      )}

      <div className="space-y-2">
        <div data-testid="mining-xp-progress-text" className="text-sm text-zinc-300">
          {profile.xp.toLocaleString()} / {profile.next_level_xp.toLocaleString()} XP
        </div>
        <Progress data-testid="mining-xp-progress-bar" value={profile.progress_pct} className="h-3 bg-zinc-900" />
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500">
          <span data-testid="mining-progress-current">This level: {profile.progress_current.toLocaleString()} / {profile.progress_needed.toLocaleString()}</span>
          <span data-testid="mining-total-xp">Total XP: {profile.total_xp.toLocaleString()}</span>
        </div>
      </div>

      {jumpLinks.length > 0 && (
        <div data-testid="mining-summary-jump-links" className="border border-zinc-800 bg-zinc-900/60 p-3 rounded-sm space-y-2">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Jump to</div>
          <div className="flex flex-wrap gap-2">
            {jumpLinks.map((link) => (
              <button
                key={link.targetId}
                type="button"
                data-testid={`mining-summary-jump-button-${link.testId}`}
                onClick={() => handleJump(link.targetId)}
                className="px-2.5 py-1 border border-amber-700/50 bg-amber-950/15 text-amber-200 hover:bg-amber-950/30 rounded-sm text-[10px] sm:text-[11px] uppercase tracking-[0.18em] transition-colors"
              >
                {link.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};